import i18next from 'i18next';
import {
  call,
  all,
  put,
  select,
  PutEffect,
  getContext,
} from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import type { Action } from 'redux-actions';
import { isApiError, requestNapi } from '@wix/da-http-client';
import { yieldToMainAsync } from '@wix/da-hooks/pkg/yieldToMain';
import type {
  PapiRequestDaSharedGallectionFoldersCreate,
  PapiDaSharedGallectionFoldersCreate,
  PapiGallection,
} from '@wix/da-papi-types';
import { withOffset, selectors } from '../../Stream';
import { normalizeGallection } from '../../redux/normalizr/helpers';
import { addErrorNotification } from '../../Notifications/redux/actionCreators';
import {
  getCurrentMediaSource,
  getCurrentFolderId,
  getNonManageablePrependedGallectionsCount,
  getGrusername,
} from '../selectors';
import {
  getGallectionStreamId,
  getGallectionDeviationStreamId,
  getGallectionType,
} from '../helpers';
import type { CreateGallectionPayload } from '../actions';
import {
  MEDIA_POPUP_GALLECTION_DEVIATION_STREAM_GROUP,
  DEVIATIONS_PER_FETCH,
} from '../constants';
import type { GallectionDeviationStreamParams } from '../types';
import { getStreamInitAction } from './initGallectionStream';

export function fakeResponse({ data }): any {
  if (process.env.NODE_ENV !== 'production') {
    const { galleries } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    const response: PapiDaSharedGallectionFoldersCreate = {
      success: true,
      gallection: { ...galleries[3], folderId: -1000, name: data.name },
    };
    return response;
  }
}

function* createGallection(
  action: Action<CreateGallectionPayload>
): SagaIterator {
  const { payload } = action;
  if (!payload) {
    return;
  }
  const mediaSource: ReturnType<typeof getCurrentMediaSource> = yield select(
    getCurrentMediaSource
  );
  const currentFolderId: ReturnType<typeof getCurrentFolderId> =
    yield select(getCurrentFolderId);
  const username: ReturnType<typeof getGrusername> =
    yield select(getGrusername);

  const gallectionsStreamId = getGallectionStreamId(
    mediaSource,
    currentFolderId
  );

  const params: PapiRequestDaSharedGallectionFoldersCreate = {
    type: getGallectionType(mediaSource) ?? 'collection',
    name: payload.name,
    username,
  };

  if (currentFolderId) {
    params['parent_folder_id'] = currentFolderId;
  }

  if (payload.folderType !== 'regular') {
    params['premium_type'] = payload.folderType;
    if (payload.folderType === 'paid') {
      params['points_price'] = payload.priceInPoints;
    }
  }

  const result: PapiDaSharedGallectionFoldersCreate | false = yield call(
    requestNapi,
    {
      method: 'post',
      url: 'gallection/folders/create',
      data: params,
    }
  );

  if (!result || !result.success || !result.gallection) {
    if (isApiError(result)) {
      const { errorDescription } = result;
      const externalDispatch = yield getContext('externalDispatch');
      yield call(yieldToMainAsync);
      yield call(
        externalDispatch,
        addErrorNotification(
          i18next.t('mediapopup.gallections.error.new', { errorDescription })
        )
      );
    }
    return;
  }

  const newGallection: PapiGallection = {
    ...result.gallection,
    parentId: currentFolderId ?? null,
    permissions: {
      createFolder: true,
      viewFolder: true,
      editFolder: true,
      deleteFolder: true,
      contributeDeviations: true,
      recommendDeviations: true,
    },
  };
  const entities = normalizeGallection(newGallection);

  const nonManageableGallectionsCount: ReturnType<
    typeof getNonManageablePrependedGallectionsCount
  > = yield select(getNonManageablePrependedGallectionsCount);

  const gallectionDeviationsStreamId = getGallectionDeviationStreamId(
    mediaSource,
    newGallection.folderId
  );

  const putActions: PutEffect<any>[] = [];

  if (currentFolderId) {
    const isGallectionStreamInitialized: ReturnType<
      typeof selectors.isInitialized
    > = yield select(selectors.isInitialized, gallectionsStreamId);
    if (!isGallectionStreamInitialized) {
      putActions.push(put(getStreamInitAction(mediaSource, currentFolderId)));
    }
  }

  const streamParams: GallectionDeviationStreamParams = {
    mediaSource,
    folderId: newGallection.folderId,
  };

  putActions.push(
    put(
      withOffset.actions.insertItems({
        streamId: gallectionsStreamId,
        items: [newGallection.folderId],
        entities,
        offset: nonManageableGallectionsCount,
      })
    ),
    put(
      withOffset.actions.initialize({
        streamId: gallectionDeviationsStreamId,
        groupId: MEDIA_POPUP_GALLECTION_DEVIATION_STREAM_GROUP,
        streamParams,
        items: [],
        itemsPerFetch: DEVIATIONS_PER_FETCH,
        hasMore: false,
      })
    )
  );

  yield all(putActions);
}

export default createGallection;
